import { HttpClient } from '@angular/common/http';
import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  NgZone,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WA_WINDOW } from '@ng-web-apis/common';
import {
  filter,
  firstValueFrom,
  fromEvent,
  interval,
  merge,
  timeout,
} from 'rxjs';
import {
  BuildMarker,
  buildMarker,
  hasMainBundle,
  sameMarker,
} from './build-marker';

export type VersionCheck = 'same' | 'different' | 'unknown';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
// Later checks join a running one, so a request stuck on a dead connection
// would hold off every check until the browser gave up on it.
const CHECK_TIMEOUT_MS = 10_000;

/**
 * Notices when the server has a different frontend build than the one this
 * tab runs, by comparing the hashed bundle names of the deployed `index.html`
 * with those of the live document.
 *
 * Checks keep running after a new version was found, so a tab that stays on
 * an old build still follows each further deploy.
 */
@Injectable({ providedIn: 'root' })
export class NewVersionService {
  private readonly http = inject(HttpClient);
  private readonly window = inject(WA_WINDOW);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  readonly ownMarker: BuildMarker = buildMarker(this.window.document);

  private readonly latest = signal<BuildMarker | null>(null);
  /** The last server build that differed from this tab's, or `null`. */
  readonly latestServerMarker = this.latest.asReadonly();
  readonly newVersionAvailable = computed(() => this.latest() !== null);

  private inFlight: Promise<VersionCheck> | null = null;
  private started = false;

  /** Checks at once, every five minutes and whenever the tab is shown again. */
  start(): void {
    if (this.started || !hasMainBundle(this.ownMarker)) {
      return;
    }
    this.started = true;
    const document = this.window.document;
    this.zone.runOutsideAngular(() => {
      void this.checkNow();
      merge(
        interval(CHECK_INTERVAL_MS),
        fromEvent(document, 'visibilitychange').pipe(
          filter(() => document.visibilityState === 'visible')
        )
      )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => void this.checkNow());
    });
  }

  /** Runs a check, or joins the one already under way. */
  checkNow(): Promise<VersionCheck> {
    if (!hasMainBundle(this.ownMarker)) {
      return Promise.resolve('unknown');
    }
    if (!this.inFlight) {
      const check = this.check();
      this.inFlight = check;
      void check.then(() => {
        if (this.inFlight === check) {
          this.inFlight = null;
        }
      });
    }
    return this.inFlight;
  }

  private async check(): Promise<VersionCheck> {
    let html: string;
    try {
      // The changing query gets past any cached copy even where the server
      // sends no caching headers.
      html = await firstValueFrom(
        this.http
          .get('index.html', {
            params: { t: String(Date.now()) },
            responseType: 'text',
          })
          .pipe(timeout(CHECK_TIMEOUT_MS))
      );
    } catch {
      return 'unknown';
    }
    const server = buildMarker(
      new DOMParser().parseFromString(html, 'text/html')
    );
    if (!hasMainBundle(server)) {
      return 'unknown';
    }
    if (sameMarker(this.ownMarker, server)) {
      // A rollback to this tab's build: there is nothing to reload into.
      if (this.latest() !== null) {
        this.zone.run(() => this.latest.set(null));
      }
      return 'same';
    }
    // Polling goes on after detection; only a further deploy is news.
    const known = this.latest();
    if (known === null || !sameMarker(known, server)) {
      this.zone.run(() => this.latest.set(server));
    }
    return 'different';
  }
}
